import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService } from '@services/auth.service';
import { LOCAL_STORAGE_KEY, StorageService } from '@services/storage.service';
import { CommonStateService } from '@app/services/common.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
    constructor(
        private readonly authService: AuthService,
        private readonly storageService: StorageService,
        private readonly commonService: CommonStateService
    ) {}

    /**
     * Intercepts HTTP requests to add authorization headers and handle errors.
     * @param request The outgoing HTTP request.
     * @param next The next handler in the chain.
     * @returns An observable of the HTTP event.
     */
    intercept<T>(
        request: HttpRequest<T>,
        next: HttpHandler
    ): Observable<HttpEvent<T>> {
        const accessToken = this.storageService.getItem(
            LOCAL_STORAGE_KEY.ACCESS_TOKEN
        );

        let authorizationHeaders: string;
        if (request.headers.has('Skip-Authorization')) {
            const clonedRequest = request.clone({
                headers: request.headers.delete('Authorization'),
            });
            return next.handle(clonedRequest);
        }

        if (
            request.url.split('/').includes('verifyOTP') ||
            request.url.split('/').includes('resendotp')
        ) {
            authorizationHeaders = `Temp ${this.storageService.getItem(
                LOCAL_STORAGE_KEY.TEMP_TOKEN
            )}`;
        } else if (
            request.url.split('/').includes('login') ||
            request.url.split('/').includes('forgotPassword')
        ) {
            authorizationHeaders = '';
        } else {
            authorizationHeaders = `Bearer ${accessToken}`;
        }

        const clonedRequest = request.clone({
            setHeaders: {
                Authorization: authorizationHeaders,
            },
        });

        return next.handle(clonedRequest).pipe(
            catchError((error: HttpErrorResponse) => {
                if (!request.url.includes('/auth')) {
                    if (error.status === 401) {
                        return this.authService.refreshToken().pipe(
                            switchMap((newAccessToken) => {
                                const retryRequest = request.clone({
                                    setHeaders: {
                                        Authorization: `Bearer ${newAccessToken.data.accessToken}`,
                                    },
                                });
                                return next.handle(retryRequest);
                            }),
                            catchError((err) => {
                                if (!err.ok) {
                                    this.commonService.showPopup();
                                }
                                return throwError(() => err);
                            })
                        );
                    } else if (error.status === 403) {
                        this.authService.logOut();
                    }
                } else if (
                    request.url.includes('/auth/verifyOTP') &&
                    error.status === 401
                ) {
                    this.authService.logOut();
                }
                return throwError(() => error);
            })
        );
    }
}
